import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { User } from 'lucide-react';
import type { Project, ProjectStatus } from '../types';
import { RiskBadge } from './RiskBadge';
import { ProjectStatusChip } from './StatusChip';
import { Markdown } from './Markdown';
import { useTicketStats } from '../hooks/useTickets';
import { ProjectProgressBar } from './ProjectProgressBar';
import { track } from '../lib/telemetry';
import {
  type TrendDirection,
  type TrendSentiment,
  riskTrendToDirection,
  directionFromSeries,
  trendSentiment,
} from '../lib/trend';
import { TrendIndicator } from './TrendIndicator';

interface RiskCardProps {
  project: Project;
}

// Header bg + divider per status (for the card's colored header band)
const HEADER_STYLE: Partial<Record<ProjectStatus, { headerBg: string; divider: string }>> = {
  at_risk:  { headerBg: 'bg-red-100 dark:bg-red-900/40',    divider: 'border-red-200 dark:border-red-900/50' },
  on_track: { headerBg: 'bg-emerald-50 dark:bg-emerald-900/30', divider: 'border-emerald-200 dark:border-emerald-900/40' },
  active:   { headerBg: 'bg-emerald-50 dark:bg-emerald-900/30', divider: 'border-emerald-200 dark:border-emerald-900/40' },
  planning: { headerBg: 'bg-blue-100 dark:bg-blue-900/40',  divider: 'border-blue-200 dark:border-blue-900/50' },
  draft:    { headerBg: 'bg-blue-100 dark:bg-blue-900/40',  divider: 'border-blue-200 dark:border-blue-900/50' },
  complete: { headerBg: 'bg-green-100 dark:bg-green-900/40', divider: 'border-green-200 dark:border-green-900/50' },
  archived: { headerBg: 'bg-gray-100 dark:bg-gray-800/60',  divider: 'border-gray-200 dark:border-white/10' },
};

function getHeaderStyle(status: ProjectStatus) {
  return HEADER_STYLE[status] ?? { headerBg: 'bg-gray-100 dark:bg-gray-800/60', divider: 'border-gray-200 dark:border-white/10' };
}

function formatRelativeTime(dateStr: string): string {
  const now = Date.now();
  const then = new Date(dateStr).getTime();
  const diff = now - then;
  const minutes = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);

  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  if (hours < 24) return `${hours}h ago`;
  if (days < 30) return `${days}d ago`;
  return new Date(dateStr).toLocaleDateString();
}

/**
 * Resolve the one trend this card displays: the backend's canonical
 * riskTrend when it exists, otherwise the shape of the drawn series (same
 * ±0.3 cut, shared with every other trend surface via lib/trend.ts). Two
 * disagreeing trends on the same project was the "Rising here, stable
 * there" bug — resolve once, render everywhere from the result.
 */
function resolveCardTrend(riskTrend: string | null | undefined, series: number[]): TrendDirection {
  const canonical = riskTrendToDirection(riskTrend);
  return canonical !== 'unknown' ? canonical : directionFromSeries(series);
}

// SVG stroke needs raw colours; keyed by sentiment so the line can never
// disagree with the label beside it. Unknown draws neutral slate.
const STROKE_BY_SENTIMENT: Record<TrendSentiment, string> = {
  positive: '#10b981', // emerald
  negative: '#f87171', // red
  neutral: '#94a3b8', // slate
  unknown: '#94a3b8',
};

function RiskSparkline({ series, direction }: { series: number[]; direction: TrendDirection }) {
  if (series.length < 2) return null;

  const W = 80;
  const H = 24;
  const MAX = 5;
  const points = series
    .map((s, i) => {
      const x = (i / (series.length - 1)) * W;
      const y = H - (s / MAX) * H;
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(' ');

  const sentiment = trendSentiment(direction, 'lower-is-better');

  return (
    <div className="flex items-center gap-2">
      <svg
        width={W}
        height={H}
        viewBox={`0 0 ${W} ${H}`}
        className="shrink-0"
        aria-hidden="true"
      >
        <polyline
          points={points}
          fill="none"
          stroke={STROKE_BY_SENTIMENT[sentiment]}
          strokeWidth="1.5"
          strokeLinejoin="round"
          strokeLinecap="round"
        />
      </svg>
      <TrendIndicator direction={direction} polarity="lower-is-better" />
    </div>
  );
}

export function RiskCard({ project }: RiskCardProps) {
  const navigate = useNavigate();
  const { headerBg, divider } = getHeaderStyle(project.status);
  const { data: ticketStats } = useTicketStats(project.projectId);
  const series = project.riskScoreSeries ?? [];
  // Same "unscored" treatment as RiskBadge (null/undefined/0 sentinel — see
  // frontend/src/lib/riskBand.ts). A project can carry leftover series
  // history (e.g. a parking lot excluded from scoring in v2026.8.3, or any
  // newly-created project that now starts unscored) without a current
  // score. Gate the sparkline on this too, not just series length — a
  // trend line ending in a real value next to a badge that says "N/A" is
  // the same contradiction as the ON TRACK / AT RISK mismatch.
  const hasScore =
    project.overallRiskScore !== null &&
    project.overallRiskScore !== undefined &&
    project.overallRiskScore !== 0;
  const trendDirection = resolveCardTrend(project.riskTrend, series);

  return (
    <motion.div
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
      transition={{ duration: 0.15 }}
      onClick={() => {
        track('project.risk_card_clicked', 'feature_use', {
          projectId: project.projectId,
          riskScore: project.overallRiskScore ?? 0,
          // Same resolved direction the card displays — telemetry must not
          // run its own third computation of the trend.
          trend: trendDirection,
        });
        navigate(`/projects/${project.projectId}`);
      }}
      className="rounded-xl border border-gray-200 dark:border-white/[0.08] shadow-sm hover:shadow-md transition-shadow cursor-pointer overflow-hidden relative"
    >
      {/* Colored header */}
      <div className={`px-5 pt-4 pb-4 ${headerBg}`}>
        <div className="flex items-center justify-between mb-3">
          <ProjectStatusChip status={project.status} />
          <div className="flex items-center gap-2">
            {project.riskCacheIsStale && (
              <span className="text-micro text-amber-600 dark:text-amber-400/70 bg-amber-100 dark:bg-amber-900/20 px-1.5 py-0.5 rounded-full border border-amber-200 dark:border-amber-900/30">
                stale
              </span>
            )}
            <RiskBadge score={project.overallRiskScore} size="lg" />
          </div>
        </div>
        <h3 className="text-title text-gray-900 dark:text-white leading-snug line-clamp-2">
          {project.name}
        </h3>
      </div>

      {/* White / dark body */}
      <div className={`px-5 py-4 bg-white dark:bg-bg-surface border-t ${divider}`}>
        {project.description && (
          <Markdown className="text-gray-500 dark:text-gray-400 line-clamp-2 mb-3">
            {project.description}
          </Markdown>
        )}

        {ticketStats && ticketStats.total > 0 && (
          <div className="mb-3">
            <ProjectProgressBar stats={ticketStats} height="sm" />
          </div>
        )}

        <div className="flex items-center justify-between pt-2 border-t border-gray-100 dark:border-white/5">
          {project.ownerName ? (
            <span className="flex items-center gap-1 text-meta text-gray-500 dark:text-gray-400">
              <User className="h-4 w-4" />
              {project.ownerName}
            </span>
          ) : <span />}
          {hasScore && series.length >= 2 ? (
            <RiskSparkline series={series} direction={trendDirection} />
          ) : (
            <span className="text-meta text-gray-400 dark:text-gray-500">
              {formatRelativeTime(project.updatedAt)}
            </span>
          )}
        </div>
      </div>

    </motion.div>
  );
}
